import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import {
  Play, Pause, SkipBack, SkipForward, X,
  Repeat, Repeat1, ChevronUp, ChevronDown,
  Eye, EyeOff, Mic, BookOpen, Type, WifiOff,
} from 'lucide-react';
import { Lottie } from 'lottie-react';
import { RECITERS } from '../../services/quranAPI';
import { REPEAT_MODES, SPEEDS } from '../../hooks/useFullAudioPlayer';
import { CrescentStarIcon } from '../common/IslamicIcons';
import { crescentPulseLottie } from '../../data/lottieAnimations';

const NASKH = "'Amiri', 'Noto Naskh Arabic', serif";
const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

const EqBars = ({ playing }) => (
  <div className="flex items-end gap-[2px]" style={{ height: 14, width: 16 }}>
    {[1,2,3,4].map(i => (
      <div key={i} className={`w-[3px] rounded-full bg-amber-400 eq-bar-${i}`}
        style={{ height: playing ? '100%' : '30%', animationPlayState: playing ? 'running' : 'paused', transition: 'height 0.2s' }} />
    ))}
  </div>
);

const QuranControlPanel = ({
  player, chapter, reciterId, setReciterId,
  arabicOnly, toggleArabicOnly, viewMode, toggleViewMode,
  readMode, setReadMode, revealedVerses, resetPractice, onVerseClick,
}) => {
  const { isDarkMode, isRTL, language } = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // Swipe gesture
  const touchStartY = useRef(null);
  const handleTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (dy < -40) setExpanded(true);
    if (dy > 40) setExpanded(false);
  };

  const {
    currentVerse, currentIdx, playing, loading, progress, elapsed, duration,
    repeatMode, speed, visible, isOffline,
    togglePlay, playSurah, playPrev, playNext, seek, cycleRepeat, cycleSpeed, stop, totalVerses,
  } = player;

  const hasAudio = !!currentVerse;
  const reciterName = RECITERS[reciterId]?.name || '';
  const isMushaf = viewMode === 'mushaf';

  const handleSetReadMode = useCallback((mode) => {
    setReadMode(prev => {
      const next = prev === mode ? 'reading' : mode;
      localStorage.setItem('quran_read_mode', next);
      return next;
    });
  }, [setReadMode]);

  // Dynamic panel bg with thicker border and nicer styling
  const panelBg = isDarkMode
    ? 'bg-gradient-to-b from-[#0d1420] to-[#111827]'
    : 'bg-white';

  return (
    <div
      data-testid="quran-control-panel"
      className={`fixed left-2 right-2 z-[140]`}
      style={{
        // Nav: bottom-7(28px) + h-16(64px) = 92px. Add 8px gap = 100px = 6.25rem
        bottom: 'calc(1.75rem + 4rem + 0.5rem)',
        transition: 'none',
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div 
        className={`rounded-[1.5rem] overflow-hidden shadow-2xl ${panelBg}`}
        style={{
          border: isDarkMode ? '2px solid rgba(255,255,255,0.15)' : '2px solid rgba(0,0,0,0.08)',
          boxShadow: isDarkMode 
            ? '0 -8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05) inset' 
            : '0 -8px 32px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.8) inset',
        }}
      >

        {/* ── Drag handle strip ── */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          {/* Left: stop audio (X) — only when audio active */}
          {hasAudio ? (
            <button
              data-testid="audio-bar-close"
              onClick={stop}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                isDarkMode ? 'bg-white/10 text-gray-300 hover:bg-red-500/20 hover:text-red-400' : 'bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500'
              }`}
              aria-label="Stop audio"
            >
              <X className="w-4 h-4" />
            </button>
          ) : <div className="w-8" />}

          {/* Center: drag handle */}
          <button
            data-testid="panel-drag-handle"
            className="flex-1 flex justify-center py-1"
            onClick={() => setExpanded(p => !p)}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <div className={`w-10 h-1 rounded-full ${isDarkMode ? 'bg-white/20' : 'bg-gray-300'}`} />
          </button>

          {/* Right: expand/collapse toggle */}
          <button
            data-testid="panel-expand-toggle"
            onClick={() => setExpanded(p => !p)}
            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
              isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-white/8' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>

        {/* ══ COLLAPSED mini strip ══ */}
        {!expanded && (
          <div className={`px-4 pb-3 flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Icon */}
            <div className="w-8 h-8 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              {hasAudio && playing
                ? <EqBars playing />
                : <Lottie src={crescentPulseLottie} loop autoplay style={{ width: 28, height: 28 }} />
              }
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {hasAudio ? (
                <>
                  <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-amber-300' : 'text-[var(--p-hex)]'}`}
                    style={{ fontFamily: NASKH, direction: 'rtl', lineHeight: 1.8 }}>
                    {currentVerse.text_uthmani?.slice(0, 35)}…
                  </p>
                  <p className={`text-[10px] tabular-nums ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} dir="ltr">
                    {chapter?.name_simple} · {currentVerse.verse_key} · {fmtTime(elapsed)}/{fmtTime(duration)}
                  </p>
                </>
              ) : (
                <>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                    {isRTL ? (chapter?.name_arabic || chapter?.name_simple || t('quranReader.quranReaderTitle')) : (chapter?.name_simple || 'Quran Reader')}
                  </p>
                  <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {arabicOnly ? t('quranReader.arabicOnly', 'Arabic Only') : t('quranReader.withTranslation', '+Translation')}
                    {' · '}{isMushaf ? t('quranReader.mushafView', 'Mushaf') : t('quranReader.cardView', 'Cards')}
                    {readMode !== 'reading' && ` · ${t(`quranMode.${readMode}`)}`}
                  </p>
                </>
              )}
            </div>

            {/* Mini controls */}
            <div className={`flex items-center gap-1.5 flex-shrink-0 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {hasAudio && (
                <>
                  <button onClick={playPrev}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}>
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button onClick={togglePlay} data-testid="main-play-btn"
                    className="w-9 h-9 rounded-xl bg-amber-500 hover:bg-amber-400 flex items-center justify-center active:scale-95 transition-all shadow-md">
                    {loading
                      ? <div className="w-3.5 h-3.5 border-2 border-[#0f172a] border-t-transparent rounded-full animate-spin" />
                      : playing ? <Pause className="w-4 h-4 text-[#0f172a]" /> : <Play className="w-4 h-4 text-[#0f172a] ml-0.5" />}
                  </button>
                  <button onClick={playNext}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center ${isDarkMode ? 'text-gray-400 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`}>
                    <SkipForward className="w-4 h-4" />
                  </button>
                </>
              )}
              {!hasAudio && (
                <button data-testid="panel-play-surah" onClick={() => playSurah(0)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-bold text-xs transition-colors active:scale-95 shadow-md">
                  <Play className="w-3.5 h-3.5" />
                  {t('quranReader.playSurah', 'Play Surah')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ══ EXPANDED full panel ══ */}
        {expanded && (
          <div className={`px-5 pb-5 space-y-4 max-h-[65vh] overflow-y-auto`} style={{ scrollbarWidth: 'none' }}>

            {/* Chapter title + audio info */}
            <div>
              <p className="font-bold text-base" style={{ color: isDarkMode ? '#fbbf24' : 'var(--p-hex)', fontFamily: NASKH }}>
                {chapter?.name_arabic || chapter?.name_simple || 'Quran'}
              </p>
              {hasAudio && (
                <p className={`text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} dir="ltr">
                  {chapter?.name_simple} · {currentVerse?.verse_key}
                  {' · '}{(currentIdx||0)+1}/{totalVerses}
                  {isOffline && <span className="ml-1 text-amber-400"> · {t('quranReader.offline', 'Offline')}</span>}
                </p>
              )}
            </div>

            {/* Reading mode */}
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('quranMode.reading', 'Reading')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'arabic', label: arabicOnly ? t('quranReader.arabicOnly', 'Arabic Only') : t('quranReader.withTranslation', '+Translation'), icon: <span style={{ fontFamily: NASKH, fontSize: '1.1rem' }}>ع</span>, action: toggleArabicOnly, active: arabicOnly },
                  { key: 'view', label: isMushaf ? t('quranReader.mushafView', 'Mushaf') : t('quranReader.cardView', 'Cards'), icon: isMushaf ? <BookOpen className="w-4 h-4" /> : <Type className="w-4 h-4" />, action: toggleViewMode, active: isMushaf },
                  { key: 'practice', label: `${t('quranMode.practice', 'Practice')}${readMode==='practice'?` ${revealedVerses?.size||0}/${totalVerses}`:''}`, icon: readMode==='practice'?<EyeOff className="w-4 h-4"/>:<Eye className="w-4 h-4"/>, action: () => handleSetReadMode('practice'), active: readMode==='practice', color: '#a78bfa' },
                  { key: 'recitation', label: t('quranMode.recitation', 'Recitation'), icon: <Mic className="w-4 h-4" />, action: () => handleSetReadMode('recitation'), active: readMode==='recitation', color: '#34d399' },
                ].map(btn => (
                  <button key={btn.key}
                    data-testid={`${btn.key}-btn`}
                    onClick={btn.action}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-2xl text-sm font-semibold border transition-colors ${
                      btn.active
                        ? isDarkMode
                          ? `border-opacity-50 text-opacity-90`
                          : `border-opacity-50 text-opacity-90`
                        : isDarkMode ? 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}
                    style={btn.active ? {
                      background: `${btn.color || 'var(--p-hex)'}22`,
                      borderColor: `${btn.color || 'var(--p-hex)'}55`,
                      color: btn.color || (isDarkMode ? '#fbbf24' : 'var(--p-hex)'),
                    } : undefined}
                  >
                    {btn.icon}
                    <span className="truncate">{btn.label}</span>
                  </button>
                ))}
              </div>
              {readMode === 'practice' && (revealedVerses?.size||0) > 0 && (
                <button onClick={resetPractice}
                  className={`mt-1.5 text-xs font-semibold ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                  {t('quranMode.resetPractice', 'Reset practice')}
                </button>
              )}
            </div>

            {/* Reciter */}
            <div>
              <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('quranReader.reciter', 'Reciter')}</p>
              <div className="flex gap-2">
                {Object.entries(RECITERS).map(([rid, info]) => (
                  <button key={rid} data-testid={`reciter-${rid}`} onClick={() => setReciterId(Number(rid))}
                    className={`flex-1 py-2.5 rounded-2xl text-xs font-semibold border transition-colors ${
                      reciterId === Number(rid)
                        ? isDarkMode ? 'bg-amber-500/20 border-amber-500/40 text-amber-300' : 'bg-amber-100 border-amber-300 text-amber-700'
                        : isDarkMode ? 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                    }`}>
                    <p className="truncate">{info.name}</p>
                    <p className="text-[10px] opacity-60 mt-0.5" style={{ fontFamily: NASKH }}>{info.nameAr}</p>
                  </button>
                ))}
              </div>
            </div>

            {!hasAudio && (
              <button data-testid="panel-play-surah-expanded" onClick={() => playSurah(0)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-amber-500 hover:bg-amber-400 text-[#0f172a] font-bold text-sm transition-colors active:scale-95 shadow-md">
                <Play className="w-5 h-5" />
                {t('quranReader.playSurah', 'Play Surah')}
              </button>
            )}

            {/* Audio player section — only when audio is active */}
            {hasAudio && (
              <div>
                <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('quranReader.nowPlaying', 'Now Playing')}</p>

                {/* Arabic verse preview */}
                <div className={`rounded-2xl p-3.5 mb-3 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                  <p className={`text-center ${isDarkMode ? 'text-amber-200' : 'text-[var(--p-hex)]'}`}
                    style={{ fontFamily: NASKH, direction: 'rtl', fontSize: '1rem', lineHeight: '2.1' }}>
                    {currentVerse?.text_uthmani?.slice(0, 100)}{(currentVerse?.text_uthmani?.length||0) > 100 ? '…' : ''}
                  </p>
                  <p className={`text-center text-[10px] mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} dir="ltr">
                    {chapter?.name_simple} · {currentVerse?.verse_key}
                  </p>
                </div>

                {/* Seek bar */}
                <div className={`relative h-2 rounded-full cursor-pointer mb-1 ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}
                  onClick={e => { const r = e.currentTarget.getBoundingClientRect(); seek((e.clientX-r.left)/r.width); }}>
                  <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-amber-400 to-amber-500"
                    style={{ width: `${(progress*100).toFixed(1)}%`, transition: 'width 0.25s linear' }} />
                  <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-amber-400 shadow-md"
                    style={{ left: `${(progress*100).toFixed(1)}%`, transform: 'translateY(-50%) translateX(-50%)' }} />
                </div>
                <div className={`flex justify-between text-[10px] tabular-nums mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} dir="ltr">
                  <span>{fmtTime(elapsed)}</span><span>{fmtTime(duration)}</span>
                </div>

                {/* Main controls */}
                <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <button data-testid="repeat-btn" onClick={cycleRepeat}
                    className={`relative w-10 h-10 rounded-2xl flex items-center justify-center ${
                      repeatMode !== 'none' ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600' : isDarkMode ? 'text-gray-500 hover:bg-white/8' : 'text-gray-400 hover:bg-gray-100'
                    }`}>
                    {repeatMode === 'one' ? <Repeat1 className="w-4 h-4" /> : <Repeat className="w-4 h-4" />}
                    {repeatMode === 'all' && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-[#0f172a] text-[8px] font-black flex items-center justify-center">∞</span>}
                  </button>

                  <button data-testid="prev-verse-btn" onClick={playPrev}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-white/8 text-gray-300 hover:bg-white/15' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    <SkipBack className="w-5 h-5" />
                  </button>

                  <button data-testid="main-play-btn" onClick={togglePlay}
                    className="w-16 h-16 rounded-3xl bg-amber-500 hover:bg-amber-400 flex items-center justify-center active:scale-95 shadow-lg shadow-amber-500/30 transition-all">
                    {loading ? <div className="w-5 h-5 border-2 border-[#0f172a] border-t-transparent rounded-full animate-spin" />
                      : playing ? <Pause className="w-6 h-6 text-[#0f172a]" /> : <Play className="w-6 h-6 text-[#0f172a] ml-0.5" />}
                  </button>

                  <button data-testid="next-verse-btn" onClick={playNext}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-white/8 text-gray-300 hover:bg-white/15' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    <SkipForward className="w-5 h-5" />
                  </button>

                  <button data-testid="speed-btn" onClick={cycleSpeed}
                    className={`w-10 h-10 rounded-2xl flex items-center justify-center text-xs font-bold ${
                      speed !== 1 ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600' : isDarkMode ? 'text-gray-500 hover:bg-white/8' : 'text-gray-400 hover:bg-gray-100'
                    }`}>
                    {speed}×
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuranControlPanel;
